/**
 * Theme state — os-internal-ui integration (docs/account-dropdown.md).
 *
 * Theme = `data-theme` on <html>. Mode = `data-mode` holds the theme's COMPANION
 * mode (plain.css convention): bare [data-theme] renders the theme's canonical
 * mode; [data-mode] activates the companion.
 *
 * The APP DEFAULT is Afterglow in "system" mode, so a fresh visit follows the
 * machine's light/dark (prefers-color-scheme) with a polished dual-mode theme
 * and live-updates when the OS setting flips. "Default (Infras)" (no data-theme)
 * is the original dark-only native look. State persists to localStorage;
 * index.html re-applies it before first paint (no-FOUC).
 *
 * Brand note: Afterglow's vendored primary is purple; apps/web theme-skins
 * Infras bridge remaps chrome accent to OptInfra blue (#3b82f6).
 */
export type ThemeName = 'mission-control' | 'afterglow' | 'reading-room';
export type Mode = 'system' | 'light' | 'dark';

export const THEME_KEY = 'infras.theme';
export const MODE_KEY = 'infras.mode';

/** App default look — a dual-mode theme so "system" gives light+dark out of the box. */
export const DEFAULT_THEME: ThemeName = 'afterglow';

export const THEME_OPTIONS: { value: ThemeName | null; label: string }[] = [
  { value: null, label: 'Default (Infras)' },
  { value: 'mission-control', label: 'Mission Control' },
  { value: 'afterglow', label: 'Afterglow' },
  { value: 'reading-room', label: 'Reading Room' },
];

export const MODE_OPTIONS: { value: Mode; label: string }[] = [
  { value: 'system', label: 'System' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

/** canonical mode per theme; the native Infras look is dark-only. */
const CANONICAL_MODE: Record<ThemeName, 'dark' | 'light'> = {
  'mission-control': 'dark',
  afterglow: 'dark',
  'reading-room': 'light',
};

const prefersDark = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;

/** Persisted theme, or the app default on a fresh visit. 'none' = user picked native. */
export function currentTheme(): ThemeName | null {
  const stored = localStorage.getItem(THEME_KEY);
  if (stored === 'none') return null;
  return (stored as ThemeName) || DEFAULT_THEME;
}

export function currentMode(): Mode {
  return (localStorage.getItem(MODE_KEY) as Mode) || 'system';
}

/** Resolve 'system' → concrete light/dark (native look is dark-only). */
export function effectiveMode(theme: ThemeName | null, mode: Mode): 'dark' | 'light' {
  if (!theme) return 'dark';
  if (mode === 'system') return prefersDark() ? 'dark' : 'light';
  return mode;
}

/** Write data-theme / data-mode to <html> — bare = canonical, [data-mode] = companion. */
export function applyTheme(theme: ThemeName | null, mode: Mode): void {
  const el = document.documentElement;
  if (theme) el.setAttribute('data-theme', theme);
  else el.removeAttribute('data-theme');
  if (!theme) {
    el.removeAttribute('data-mode'); // native = canonical dark
    return;
  }
  const rm = effectiveMode(theme, mode);
  if (rm === CANONICAL_MODE[theme]) el.removeAttribute('data-mode');
  else el.setAttribute('data-mode', rm);
}

export function setTheme(theme: ThemeName | null): void {
  localStorage.setItem(THEME_KEY, theme ?? 'none');
  applyTheme(theme, currentMode());
}

export function setMode(mode: Mode): void {
  localStorage.setItem(MODE_KEY, mode);
  applyTheme(currentTheme(), mode);
}

/** Live-follow the OS while in 'system' mode. Call once at startup. */
export function watchSystemMode(): void {
  if (typeof window === 'undefined') return;
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (currentMode() === 'system') applyTheme(currentTheme(), 'system');
  });
}
